'use client';

import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

let client: ReturnType<typeof createBrowserClient> | null = null;

function getSupabaseEnv() {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY');
  }
  return { url, anonKey };
}

/** Detect whether third-party / partitioned cookies work (fails inside many iframes). */
const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const eqIndex = c.trim().indexOf('=');
          const name = eqIndex !== -1 ? c.trim().slice(0, eqIndex) : c.trim();
          const rest = eqIndex !== -1 ? c.trim().slice(eqIndex + 1) : '';
          return { name: name.trim(), value: decodeURIComponent(rest) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: { path?: string; maxAge?: number; domain?: string; expires?: Date | string | number }) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);
  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];
  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage()).find((c) =>
    c.name.includes('auth-token')
  )?.value ?? null;

/** Attach auth token on same-origin fetches so middleware can restore the session in iframes. */
if (typeof window !== 'undefined' && !(window as Window & { __sb_patched__?: boolean }).__sb_patched__) {
  (window as Window & { __sb_patched__?: boolean }).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
      const headers = new Headers(
        init?.headers || (input instanceof Request ? input.headers : undefined)
      );
      headers.set('x-sb-token', token);
      init = { ...(init || {}), headers };
    }
    return orig(input, init);
  };
}

/**
 * Browser Supabase client (singleton).
 * Uses cookies when available; falls back to localStorage + x-sb-token for iframe embeds.
 */
export function createClient() {
  if (client) return client;
  const { url, anonKey } = getSupabaseEnv();
  client = createBrowserClient(url, anonKey, {
    cookies: {
      getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
      setAll(cookiesToSet) {
        if (typeof document === 'undefined') return;
        if (canUseCookies()) {
          cookiesToSet.forEach(({ name, value, options }) =>
            value ? setCookie(name, value, options) : deleteCookie(name)
          );
        } else {
          cookiesToSet.forEach(({ name, value, options }) => {
            try {
              value
                ? localStorage.setItem(`${PFX}${name}`, value)
                : localStorage.removeItem(`${PFX}${name}`);
            } catch {
              // private mode / storage blocked
            }
            if (value) setCookie(name, value, options);
          });
        }
      },
    },
  });
  return client;
}
